import { Modal, Tag, message } from 'antd'
import { executeBooking, getBookingState } from '../Bookings/utils/bpm-utils'
import Store from '../../store'
import adminBookingsReducer from '../Admin/bookings/utils/reducer'
import todaysPatientsReducer from '../Dashboard/Employee/components/todays-patients/todays-patients-reducer'
import billingReducer from '../Bookings/utils/billing-reducer'
import EmployeeBookingsReducer from '../Bookings/utils/employee-bookings-reducer'
import { getBpmStatusLabel, handleError } from './utils'
import CoreEnums from '../../core/utils/enums'
import { readBookingsAggregate } from '../Admin/bookings/utils/utils'
import dayjs from 'dayjs'
import AppointmentsCalendarReducer from '../Appointments-Calendar/utils/appointments-calendar-reducer'
import { reverseCallpayOriginalTransaction } from '../PeachPayments/utils/utils'
import bookingsReducer from '../Bookings/utils/booking-reducer'

export const handleCancelBooking = (authState, record, updateBooking, onAfterCancel, token) => {
  // Debug logging to understand the cancellation attempt
  console.log('🔍 handleCancelBooking called:', {
    userType: authState.agiliteUser?.extraData?.role?.type,
    recordDateCreated: record.dateCreated,
    recordId: record._id,
    recordStatus: record.status
  })

  // Early status checks before time validation
  if (record.status === CoreEnums.bookingStatuses.cancelled) {
    console.log('🚫 Cancellation blocked - appointment already cancelled')
    message.error({
      content: 'Cancellation Not Allowed: This appointment is already cancelled.',
      duration: 8
    })
    return
  }

  if (record.status === CoreEnums.bookingStatuses.completed) {
    console.log('🚫 Cancellation blocked - appointment already completed')
    message.error({
      content: 'Cancellation Not Allowed: This appointment is already completed.',
      duration: 8
    })
    return
  }

  if (record.wasNoShow) {
    console.log('🚫 Cancellation blocked - appointment marked as no-show')
    message.error({
      content: 'Cancellation Not Allowed: This appointment is marked as no-show.',
      duration: 8
    })
    return
  }

  // Check if appointment is within 3-hour cancellation window (only for patients)
  if (authState.agiliteUser?.extraData?.role?.type === 'patient') {
    // Validate that appointment date/time exists
    if (!record.bookingDate || !record.startTime) {
      console.log('🚫 No appointment date/time found on booking record, skipping time validation')
    } else {
      // Create appointment datetime more robustly
      const appointmentDate = dayjs(record.bookingDate)
      const [hours, minutes] = record.startTime.split(':')
      const appointmentDateTime = appointmentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)

      console.log('🔍 Booking-utils parsing:', {
        rawDate: record.bookingDate,
        rawTime: record.startTime,
        parsedDate: appointmentDate.format(),
        parsedDateTime: appointmentDateTime.format(),
        isValid: appointmentDateTime.isValid()
      })

      // Validate that the dayjs object is valid
      if (!appointmentDateTime.isValid()) {
        console.log('🚫 Invalid appointment date/time on booking record, skipping time validation')
      } else {
        const currentTime = dayjs()
        const hoursUntilAppointment = appointmentDateTime.diff(currentTime, 'hours', true)
        const appointmentInPast = appointmentDateTime.isBefore(currentTime)

        console.log('🔍 Time calculation for patient cancellation:', {
          appointmentDateTime: appointmentDateTime.format(),
          currentTime: currentTime.format(),
          hoursUntilAppointment: hoursUntilAppointment.toFixed(2),
          appointmentInPast: appointmentInPast,
          tooClose: hoursUntilAppointment < 3
        })

        if (appointmentInPast) {
          console.log('🚫 Cancellation blocked - appointment has already passed')
          message.error({
            content: 'Cancellation Not Allowed: This appointment has already passed.',
            duration: 8
          })
          return
        }

        if (hoursUntilAppointment < 3) {
          console.log('🚫 Cancellation blocked - appointment too close (< 3 hours)')

          // Use message.error to avoid context issues
          message.error({
            content: `Cancellation Not Allowed: Your appointment is in ${hoursUntilAppointment.toFixed(
              1
            )} hours. Appointments must be cancelled at least 3 hours in advance.`,
            duration: 8
          })
          return
        }
      }
    }
  }

  let disabled = false
  Modal.confirm({
    title: 'Cancel Booking',
    className: token.themeControl,
    okButtonProps: { style: { background: token.colorSuccess, color: 'white' } },
    cancelButtonProps: { style: { background: token.colorError, color: 'white' } },
    onCancel: async () => {
      function delayCancel() {
        message.warning('Still processing cancellation, please wait.')
        return new Promise((resolve) => {
          if (!disabled) {
            return resolve
          }
        })
      }
      if (disabled) {
        await delayCancel()
      }
    },
    content: 'Are you sure you want to cancel this booking?',
    okText: 'Yes',
    cancelText: 'No',

    onOk: async () => {
      disabled = true
      try {
        console.log('🔍 Proceeding with cancellation for:', {
          processRef: record.processRef,
          userType: authState.agiliteUser?.extraData?.role?.type,
          recordId: record._id
        })

        let processRecord = await getBookingState([record.processRef])
        console.log('🔍 BPM state retrieved:', processRecord)

        // Prepare BPM parameters with validation
        const currentUser = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`
        const bpmParams = {
          bpmKey: 'patient_journey',
          bpmRecordId: record.processRef,
          optionSelected: 'cancel',
          currentUser: currentUser,
          currentStep: processRecord.key,
          nextStepKey: 'cancelled'
        }

        console.log('🔍 BPM execution parameters:', bpmParams)

        // BPM - Try to execute, but handle failures gracefully
        try {
          processRecord = await executeBooking(
            bpmParams.bpmKey,
            bpmParams.bpmRecordId,
            bpmParams.optionSelected,
            bpmParams.currentUser,
            bpmParams.currentStep,
            bpmParams.nextStepKey
          )
          console.log('🔍 BPM execution successful:', processRecord)
        } catch (bpmError) {
          console.log('🚫 BPM execution failed, proceeding with direct cancellation:', bpmError)
          // Create a mock process record for status mapping
          processRecord = {
            key: CoreEnums.bookingStatuses.cancelled
          }
        }

        // Callpay
        if (record.callpayGatewayReference) {
          await reverseCallpayOriginalTransaction(record.callpayGatewayReference)
        }

        // Determine the status to update - use bmpConfig mapping if available, otherwise use cancelled directly
        let statusToUpdate = CoreEnums.bookingStatuses.cancelled

        if (record.clinicRecord?.bmpConfig && processRecord.key) {
          const bmpMapping = record.clinicRecord.bmpConfig.find((e) => e.key === processRecord.key)
          if (bmpMapping?.key) {
            statusToUpdate = bmpMapping.key
          }
        }

        console.log('🔍 Updating booking status to:', statusToUpdate)

        let newRecord = await updateBooking({ status: statusToUpdate }, { _id: record._id })
        updateAllBookingsState(newRecord)
        onAfterCancel()
        message.success('Booking Cancelled')
      } catch (e) {
        message.error(`Unable to cancel booking. ${handleError(e, true)}`)
      }
      disabled = false
    }
  })
}

export const handleWasNoShow = (record, updateBooking, handleGetData, token) => {
  Modal.confirm({
    title: 'Booking No Show',
    className: token.themeControl,
    okButtonProps: { style: { background: token.colorSuccess, color: 'white' } },
    cancelButtonProps: { style: { background: token.colorError, color: 'white' } },
    content: 'Are you sure you want to mark this booking as a No Show? This action can`t be reversed.',
    okText: 'Yes',
    cancelText: 'No',
    onOk: async () => {
      let newRecord = await updateBooking({ wasNoShow: true }, { _id: record._id })
      updateAllBookingsState(newRecord)
      // handleGetData()
    }
  })
}

export const generateStatus = (record, status, token, noShow, textOnly) => {
  console.log('Status', status)
  let tagColor = null
  let statusText = null
  if (noShow) {
    tagColor = token.colorTextDisabled
    status = 'No Show'
  } else {
    switch (status) {
      case 'procedure':
        // statusText = getBpmStatusLabel(record.clinicRecord, status)
        statusText = 'Procedure'
        tagColor = token.colorSecondary
        break
      case 'diagnosis':
        // statusText = getBpmStatusLabel(record.clinicRecord, status)
        statusText = 'Diagnosis'
        tagColor = token.colorWarning
        break
      case 'new_booking':
        statusText = 'Check-in'
        tagColor = token.colorPrimary
        break
      case 'check_in':
        statusText = 'Check-in'
        tagColor = token.colorPrimary
        break
      case CoreEnums.bookingStatuses.checkin:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Check-in'
        tagColor = token.colorPrimaryText
        break
      case CoreEnums.bookingStatuses.screening:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Screening'
        tagColor = token.colorSecondary
        break
      case CoreEnums.bookingStatuses.consultation:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Consultation'
        tagColor = token.colorWarning
        break
      case CoreEnums.bookingStatuses.reception:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Reception'
        tagColor = token.colorInfo
        break
      case CoreEnums.bookingStatuses.completed:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Completed'
        tagColor = token.colorSuccess
        break
      case CoreEnums.bookingStatuses.cancelled:
        statusText = getBpmStatusLabel(record?.clinicRecord, status) || 'Cancelled'
        tagColor = token.colorError
        break
      default:
        statusText = 'Unknown'
        tagColor = undefined
    }
  }
  if (textOnly) {
    return statusText
  } else {
    return <Tag color={tagColor}>{statusText}</Tag>
  }
}

export const updateAllBookingsState = (record) => {
  const userState = Store.getState().auth.agiliteUser
  let adminBookings = JSON.parse(JSON.stringify(Store.getState().adminBookings.data))
  let todaysBookings = JSON.parse(JSON.stringify(Store.getState().todaysPatients.data))
  let employeeBookings = JSON.parse(JSON.stringify(Store.getState().employeeBookings.data))
  let billingBookings = JSON.parse(JSON.stringify(Store.getState().billing.data))
  let calendarBookings = JSON.parse(JSON.stringify(Store.getState().appointmentsCalendar.data))
  let dashboardBookings = JSON.parse(JSON.stringify(Store.getState().bookings.dashboardData))

  let adminBookIndex = -1
  let todaysBookingsIndex = -1
  let employeeBookingsIndex = -1
  let billingIndex = -1
  let calendarIndex = -1
  let dashboardIndex = -1

  adminBookIndex = adminBookings.findIndex((x) => x._id === record._id)
  todaysBookingsIndex = todaysBookings.findIndex((x) => x._id === record._id)
  employeeBookingsIndex = employeeBookings.findIndex((x) => x._id === record._id)
  billingIndex = billingBookings.findIndex((x) => x._id === record._id)
  calendarIndex = calendarBookings.findIndex((x) => x._id === record._id)
  dashboardIndex = dashboardBookings.findIndex((x) => x._id === record._id)

  // Handle Admin Bookings State Change
  if (adminBookIndex !== -1) {
    adminBookings[adminBookIndex] = { ...adminBookings[adminBookIndex], ...record }
    Store.dispatch(adminBookingsReducer.actions.setRecords(adminBookings))
  }

  // Handle Dashboard Bookings State Change
  if (dashboardIndex !== -1) {
    dashboardBookings[dashboardIndex] = { ...dashboardBookings[dashboardIndex], ...record }
    Store.dispatch(bookingsReducer.actions.setDashboardRecords(dashboardBookings))
  }

  // Handle Employee Bookings State Change
  if (employeeBookingsIndex !== -1) {
    employeeBookings[employeeBookingsIndex] = { ...employeeBookings[employeeBookingsIndex], ...record }
    Store.dispatch(EmployeeBookingsReducer.actions.setRecords(employeeBookings))
  }

  if (calendarIndex !== -1) {
    calendarBookings[calendarIndex] = { ...calendarBookings[calendarIndex], ...record }
    Store.dispatch(AppointmentsCalendarReducer.actions.setRecords(calendarBookings))
  }

  // TODO: Handle Billing State
  // Records not in the billing stage must be spliced
  // Records moved into the billing stage must be pushed into the billing state
  if (billingIndex !== -1) {
    if (record.status === CoreEnums.bookingStatuses.reception) {
      billingBookings[billingIndex] = { ...billingBookings[billingIndex], ...record }
    } else {
      billingBookings.splice(billingIndex, 1)
    }

    Store.dispatch(billingReducer.actions.setRecords(billingBookings))
  }

  // Handle Todays Patients Component
  // Todays patients component only shows records that are in the relevant stage for the user
  // This means that some records are instead spliced when they are no longer relevant
  if (todaysBookingsIndex !== -1) {
    if (
      record.status === CoreEnums.bookingStatuses.cancelled ||
      record.wasNoShow ||
      record.status === CoreEnums.bookingStatuses.completed
    ) {
      todaysBookings.splice(todaysBookingsIndex, 1)
    } else {
      if (userState.extraData.role.type === 'medical_professional') {
        if (userState.extraData.profession === 'nurse') {
          if (record.status !== CoreEnums.bookingStatuses.screening) {
            todaysBookings.splice(todaysBookingsIndex, 1)
          } else {
            todaysBookings[todaysBookingsIndex] = { ...todaysBookings[todaysBookingsIndex], ...record }
          }
        }
        if (userState.extraData.profession === 'doctor') {
          if (record.status !== CoreEnums.bookingStatuses.consultation) {
            todaysBookings.splice(todaysBookingsIndex, 1)
          } else {
            todaysBookings[todaysBookingsIndex] = { ...todaysBookings[todaysBookingsIndex], ...record }
          }
        }
      }
    }

    Store.dispatch(todaysPatientsReducer.actions.setRecords(todaysBookings))
  }
}

export const findPreviousLatestBooking = async (booking) => {
  let tmpData = []
  let qry = null

  try {
    qry = {
      userRef: booking.userRef,
      dateCreated: { $lt: dayjs(new Date(booking.dateCreated)).format() },
      status: 'completed'
    }
    tmpData = await readBookingsAggregate(qry)

    tmpData.sort((a, b) => {
      return new Date(b.dateCreated) - new Date(a.dateCreated)
    })

    if (tmpData.length > 0) {
      return tmpData[0]
    } else {
      return null
    }
  } catch (e) {
    throw new Error(e)
  }
}
